/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: adminPage.tsx
 *
 *  author: Thomas CLERET (thomas.cleret@proton.me)
 *
 *  creation: 29/10/2025 - 10:06
 *
 *  description:
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */

import * as React from "react";
import { toast } from "react-toastify";
import * as utils from "../utils/utils.tsx";
import DataTable from "react-data-table-component";

/**
 * Administration page of the website
 *
 * Allows to chage the access database content from the page
 * Pages (group, user, domain) are displayed as lists
 *
 * @returns the corresponding JSX object
 */
export default function AdminPage() {
   const [data, setData] = React.useState({ groups: [], users: [], domains: [] });
   const [selectedGroup, setSelectedGroup] = React.useState([]);
   const [selectedUser, setSelectedUser] = React.useState([]);
   const [selectedDomain, setSelectedDomain] = React.useState([]);
   const [dialogContent, setDialogContent] = React.useState("");

   // Display parameters for the 3 tables
   const groupsListColumns = [
      { name: "Id", selector: (row) => row.id, sortable: true, width: "100px", grow: 0 },
      { name: "Nom", selector: (row) => row.group, sortable: true, width: "200px", grow: 0 },
      { name: "Domaines", selector: (row) => row.actions, sortable: true, grow: 1 },
   ];
   const usersListColumns = [
      { name: "Id", selector: (row) => row.id, sortable: true, width: "100px", grow: 0 },
      { name: "Login", selector: (row) => row.username, sortable: true, width: "250px", grow: 0 },
      { name: "Groupe", selector: (row) => row.group, sortable: true, width: "150px", grow: 0 },
      { name: "Domaines", selector: (row) => row.domains, grow: 1 },
   ];
   const domainsListColumns = [
      { name: "Id", selector: (row) => row.id, sortable: true, width: "100px", grow: 0 },
      { name: "Nom", selector: (row) => row.domain, sortable: true, grow: 1 },
   ];
   const paginationComponentOptions = {
      rowsPerPageText: "Utilisateurs par page",
      rangeSeparatorText: "sur",
      selectAllRowsItem: true,
      selectAllRowsItemText: "Tous",
   };

   // Fill the lists to display
   async function populateLists() {
      // Get connexion token if existing
      try {
         let reply = await utils.apiClient.get("/adminAction/groups");
         const groups = reply.data.data;

         reply = await utils.apiClient.get("/adminAction/users");
         const users = reply.data.data;

         reply = await utils.apiClient.get("/adminAction/domains");
         const domains = reply.data.data;

         setData({ groups: groups, users: users, domains: domains });
      } catch (error) {
         toast.error(error);
      }
   }

   // Callbacks for the datatables to keep track of the selected rows
   const handleSelectedGroup = React.useCallback((state) => {
      setSelectedGroup(state.selectedRows);
   }, []);
   const handleSelectedUser = React.useCallback((state) => {
      setSelectedUser(state.selectedRows);
   }, []);
   const handleSelectedDomain = React.useCallback((state) => {
      setSelectedDomain(state.selectedRows);
   }, []);

   // --------------------------------------------------------------------------
   //  Display logic for the action menus
   // --------------------------------------------------------------------------

   function displayActionButtons(selectedElementSize, dataType) {
      let title = "";
      if (dataType == "users") {
         title = "Utilisateurs";
      } else if (dataType == "groups") {
         title = "Groupes";
      } else {
         title = "Domaines";
      }

      if (selectedElementSize != 0) {
         return (
            <div className="table-header">
               <div className="table-title">{title}</div>
               <div className="table-button-div">
                  <button className="table-button-add" key="add" onClick={() => setDialogWindow(dataType, "add")}>
                     Ajouter
                  </button>
                  <button className="table-button-edit" key="edit" onClick={() => setDialogWindow(dataType, "edit")}>
                     {" "}
                     Modifier
                  </button>
                  <button
                     className="table-button-delete"
                     key="delete"
                     onClick={() => setDialogWindow(dataType, "delete")}
                  >
                     {" "}
                     Supprimer
                  </button>
               </div>
            </div>
         );
      } else {
         return (
            <div className="table-header">
               <div className="table-title">{title}</div>
               <div className="table-button-div">
                  <button className="table-button-add" key="add" onClick={() => setDialogWindow(dataType, "add")}>
                     Ajouter
                  </button>
                  <button className="table-button-disabled" key="edit">
                     Modifier
                  </button>
                  <button className="table-button-disabled" key="delete">
                     Supprimer
                  </button>
               </div>
            </div>
         );
      }
   }

   function closeDialogContent() {
      setDialogContent("");
   }

   /**
    * Generate the correct modal window to handle mutation of the DB data
    *
    * @param {string} dataType groups/users/domains
    * @param {string} actionType add/edit/delete
    */
   function setDialogWindow(dataType, actionType) {
      let inputs = "";
      let title = "";
      let setDefaultValues = true;

      if (actionType == "add") {
         title = "Créer ";
         setDefaultValues = false;
      } else if (actionType == "edit") {
         title = "Modifier ";
      } else {
         title = "Supprimer ";
      }

      // Create the form
      if (dataType == "groups") {
         // Get default values
         let group_name = "";
         let group_actions = "";
         if (setDefaultValues) {
            group_name = selectedGroup[0].group;
            group_actions = selectedGroup[0].actions;
         }

         // Form data
         inputs = (
            <div className="flex justify-end space-x-3">
               <input
                  id="group_name"
                  placeholder="Nom"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blus-500"
                  defaultValue={group_name}
               />
               <input
                  id="group_actions"
                  placeholder='["Actions1", "Action2"]'
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  defaultValue={group_actions}
               />
            </div>
         );
         title += "un groupe";
      } else if (dataType == "users") {
         // Get default values
         let username = "";
         let password = "";
         let group = "";
         let domains = "";
         if (setDefaultValues) {
            username = selectedUser[0].username;
            password = selectedUser[0].password;
            group = selectedUser[0].group;
            domains = selectedUser[0].domains;
         }

         // Form data
         inputs = (
            <div className="modal-container justify-end space-x-3">
               <text> Login </text>
               <input
                  id="user_name"
                  placeholder="Login"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blus-500"
                  defaultValue={username}
               />
               <text> Mot de passe </text>
               <input
                  id="user_password"
                  type="password"
                  placeholder="Mot de passe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blus-500"
                  defaultValue={password}
               />
               <text> Groupe </text>
               <input
                  id="user_group"
                  placeholder="Groupe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blus-500"
                  defaultValue={group}
               />
               <text> Domaines </text>
               <input
                  id="user_domains"
                  placeholder='["Domain1", "Domain2"]'
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  defaultValue={domains}
               />
            </div>
         );
         title += "un utilisateur";
      } else {
         // Get default values
         let domain_id = "";
         let domain_name = "";
         if (setDefaultValues) {
            domain_id = selectedDomain[0].id;
            domain_name = selectedDomain[0].domain;
         }

         // Form data
         if (actionType == "edit") {
            inputs = (
               <div className="flex justify-end space-x-3">
                  <input
                     id="domain_id"
                     placeholder="Id"
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blus-500"
                     defaultValue={domain_id}
                  />
                  <input
                     id="domain_name"
                     placeholder="Nom"
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blus-500"
                     defaultValue={domain_name}
                  />
               </div>
            );
         } else {
            inputs = (
               <div className="flex justify-end space-x-3">
                  <input
                     id="domain_name"
                     placeholder="Nom"
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blus-500"
                     defaultValue={domain_name}
                  />
               </div>
            );
         }
         title += "un domaine";
      }

      setDialogContent(
         <div className="fixed inset-0 delete-overlay flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
               <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                  {inputs}
               </div>
               <div className="flex justify-end space-x-3">
                  <button
                     id="cancel"
                     className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                     onClick={closeDialogContent}
                  >
                     Annuler
                  </button>
                  <button
                     id="validate"
                     className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                     onClick={() => actionCallback(dataType, actionType)}
                  >
                     Valider
                  </button>
               </div>
            </div>
         </div>
      );
   }

   /**
    * Perform the action: send data to the back-end
    *
    * @param {string} dataType groups/users/domains
    * @param {string} actionType add/edit/delete
    */
   async function actionCallback(dataType, actionType) {
      // Get the for data for the request
      let form_data;
      let url = "";
      if (dataType == "groups") {
         form_data = {
            name: document.getElementById("group_name").value,
            actions: document.getElementById("group_actions").value,
         };
         url = "/adminAction/groups";
      } else if (dataType == "users") {
         form_data = {
            username: document.getElementById("user_name").value,
            password: document.getElementById("user_password").value,
            group: document.getElementById("user_group").value,
            domains: document.getElementById("user_domains").value,
         };
         url = "/adminAction/users";
      } else {
         if (actionType == "edit") {
            form_data = {
               name: document.getElementById("domain_name").value,
               id: document.getElementById("domain_id").value,
            };
         } else {
            form_data = { name: document.getElementById("domain_name").value, id: 0 };
         }
         url = "/adminAction/domains";
      }

      try {
         if (actionType == "add") {
            await utils.apiClient.put(url, { element: form_data });
         } else if (actionType == "edit") {
            await utils.apiClient.post(url, { element: form_data });
         } else if (actionType == "delete") {
            await utils.apiClient.delete(url, { data: { element: form_data } });

            if (dataType == "users") {
               setSelectedUser([]);
            } else if (dataType == "groups") {
               setSelectedGroup([]);
            } else {
               setSelectedDomain([]);
            }
         }
         populateLists();
      } catch (error) {
         toast.error(error);
      }
      closeDialogContent();
   }

   // --------------------------------------------------------------------------
   //  Handlers for the action menus
   // --------------------------------------------------------------------------

   const contextActionsGroup = React.useMemo(() => {
      return displayActionButtons(selectedGroup.length, "groups");
   }, [selectedGroup]);

   const contextActionsUser = React.useMemo(() => {
      return displayActionButtons(selectedUser.length, "users");
   }, [selectedUser]);

   const contextActionsDomain = React.useMemo(() => {
      return displayActionButtons(selectedDomain.length, "domains");
   }, [selectedDomain]);

   // --------------------------------------------------------------------------
   //  First execution code
   // --------------------------------------------------------------------------

   if (data.groups.length <= 0) {
      populateLists();
   }

   return (
      <div className="action_content">
         {dialogContent}
         <DataTable
            columns={groupsListColumns}
            data={data.groups}
            highlightOnHover
            striped
            dense
            subHeader
            subHeaderComponent={contextActionsGroup}
            selectableRows
            selectableRowsSingle
            selectableRowsHighlight
            onSelectedRowsChange={handleSelectedGroup}
         />
         <DataTable
            columns={usersListColumns}
            data={data.users}
            highlightOnHover
            striped
            dense
            subHeader
            subHeaderComponent={contextActionsUser}
            selectableRows
            selectableRowsSingle
            selectableRowsHighlight
            onSelectedRowsChange={handleSelectedUser}
            pagination
            paginationComponentOptions={paginationComponentOptions}
         />
         <DataTable
            columns={domainsListColumns}
            data={data.domains}
            highlightOnHover
            striped
            dense
            subHeader
            subHeaderComponent={contextActionsDomain}
            selectableRows
            selectableRowsSingle
            selectableRowsHighlight
            onSelectedRowsChange={handleSelectedDomain}
         />
      </div>
   );
}
